<template>
  <CustomModal
    title="Add Caller ID"
    width="32vw"
    v-model:visible="internalVisible"
    :closable="currentStep === addCallerIDSteps.ADD_DETAILS"
  >
    <div id="add_caller_id" class="flex flex-col proxima_nova">
      <div
        v-if="currentStep === addCallerIDSteps.ADD_DETAILS"
        class="flex flex-col gap-6"
      >
        <div id="call_id_number" class="flex flex-col gap-1">
          <span class="secondary_color text-[13px]">Caller ID Number</span>
          <vue-tel-input
            v-model="formData.phoneNumber"
            :defaultCountry="defaultCountryCode"
            :dropdownOptions="{
              showFlags: true,
              showDialCodeInList: true,
              showSearchBox: true,
              searchBoxPlaceholder: 'Search country..',
            }"
            mode="international"
            :inputOptions="{
              placeholder: 'Enter phone number',
            }"
          />
        </div>

        <div id="extension" class="flex flex-col gap-1">
          <div class="flex flex-row items-center gap-1">
            <span class="secondary_color text-[13px]">Extension</span>

            <a-tooltip placement="top" :title="callTooltipContent.EXTENSION">
              <ThemeImage
                :icon="imageNames.INFO_GREY"
                alt="info"
                class="size-4"
              />
            </a-tooltip>
          </div>

          <a-input
            v-model:value="formData.extension"
            class="input_medium"
            placeholder="Enter extension"
          />
        </div>
      </div>

      <div
        v-if="currentStep === addCallerIDSteps.VERIFICATION"
        class="flex flex-col gap-6 items-center"
      >
        <div class="flex flex-col gap-4 items-center w-fit">
          <span class="text-[13px] primary_color">
            Please enter the code when you receive a call from us to verify your
            number. Click on "Verify" once you have entered the code correctly.
          </span>

          <div
            v-if="provider === telephonyProviders.twilio"
            class="flex items-center justify-center w-fit px-3 py-2 text-2xl rounded-lg font-semibold bg_4"
          >
            {{ formData.validationCode || "000000" }}
          </div>

          <div v-if="provider === telephonyProviders.plivoInternational">
            <span> </span>
            <OTPFields
              source="add_caller_id_modal"
              v-model:value="formData.validationCode"
              :length="4"
              @update:enter="handleVerification"
            />
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex flex-row w-full justify-between items-center">
        <a-button @click="closeModal" class="secondary_medium">
          Cancel
        </a-button>

        <div class="flex flex-row items-center gap-2">
          <template v-if="currentStep === addCallerIDSteps.ADD_DETAILS">
            <a-button
              class="primary_medium"
              :loading="verifying"
              :disabled="disableVerifyButton"
              @click="toCreateCallerID"
            >
              {{ verifying ? "Verifying..." : "Verify caller ID" }}
            </a-button>
          </template>

          <template v-else>
            <div class="flex flex-row items-center gap-2">
              <a-button class="secondary_medium" @click="handleBack">
                Back
              </a-button>

              <a-button
                class="primary_medium"
                :loading="verifying"
                @click="handleVerification"
              >
                {{ verifying ? "Verifying..." : "Verify & Add" }}
              </a-button>
            </div>
          </template>
        </div>
      </div>
    </template>
  </CustomModal>
</template>

<script setup>
import { ref, reactive, watch, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { message } from "ant-design-vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { getCountryCode } from "@/src/utils/phoneNumberUtils.js";
import { callTooltipContent } from "@/src/constants/callConstants.js";
import { telephonyProviders } from "@/src/constants/telephonyConstants.js";
import {
  addCallerIDStates,
  addCallerIDStatuses,
  addCallerIDSteps,
  countryMapping,
} from "@/src/constants/phoneNumberConstants.js";
import eventBus from "@/src/utils/eventBus.js";
import CallService from "@/src/api/CallService.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import CustomModal from "@/src/components/CustomModal.vue";
import OTPFields from "@/src/components/security_components/OTPFields.vue";

const store = useStore();
const defaultCountryCode = computed(() => store.getters["workspace/defaultCountryCode"]);

onMounted(() => {
  store.dispatch("workspace/fetchDefaultCountryCode");
});

const props = defineProps({
  visible: { type: Boolean, required: true },
  provider: { type: String, default: telephonyProviders.twilio },
});

const emit = defineEmits(["update:visible"]);

const internalVisible = ref(props.visible);
const verifying = ref(false);
const currentStep = ref(addCallerIDSteps.ADD_DETAILS);

const formData = reactive({
  phoneNumber: "",
  extension: "",
  callerIDID: null,
  validationCode: "",
  verificationUUID: null,
});

const disableVerifyButton = computed(() => {
  return !formData.phoneNumber;
});

const closeModal = () => {
  try {
    internalVisible.value = false;
    resetForm();
  } catch (error) {
    console.error("An error occurred while closing modal: ", error);
  }
};

const handleBack = () => {
  try {
    currentStep.value = addCallerIDSteps.ADD_DETAILS;
    formData.validationCode = "";
  } catch (error) {
    console.error("An error occurred while handling back: ", error);
  }
};

const resetForm = () => {
  try {
    currentStep.value = addCallerIDSteps.ADD_DETAILS;
    Object.assign(formData, {
      phoneNumber: "",
      extension: "",
      callerIDID: null,
      validationCode: "",
      verificationUUID: null,
    });
    verifying.value = false;
  } catch (error) {
    console.error("An error occurred while resetting form: ", error);
  }
};

const handleVerification = async () => {
  try {
    verifying.value = true;

    if (props.provider === telephonyProviders.twilio) {
      await toGetCallerIDbyID();
    }

    if (props.provider === telephonyProviders.plivoInternational) {
      await toVerifyPlivoCallerID();
    }
  } catch (error) {
    console.error("An error occurred while handling verification: ", error);
  } finally {
    verifying.value = false;
  }
};

const toVerifyPlivoCallerID = async () => {
  try {
    const payload = {
      otp: formData.validationCode,
      callerid_id: formData.callerIDID,
      verification_uuid: formData.verificationUUID,
    };

    const response = await CallService.verifyPlivoCallerID(payload);
    if (response?.success) {
      const data = response?.data;

      if (data?.status) {
        message.success("Caller ID verification is verified!");
        eventBus.emit("caller-id-added");
        closeModal();
      } else {
        message.error(data?.message);
      }
    }
  } catch (error) {
    console.error("An error occurred while verifying caller ID OTP: ", error);
    message.error(error?.data?.error || "Caller ID was not verified!");
  }
};

const toGetCallerIDbyID = async () => {
  try {
    const response = await CallService.getCallerIDbyID(formData.callerIDID);
    if (response?.success) {
      const data = response.data;

      if (data?.status === addCallerIDStatuses.PENDING) {
        message.info("Caller ID verification is still in progress!");
      } else if (data?.status === addCallerIDStatuses.SUCCESS) {
        message.success("Caller ID verified and added successfully!");
        eventBus.emit("caller-id-added");
        closeModal();
      } else if (data?.status === addCallerIDStatuses.FAILED) {
        message.error("Caller ID verification failed. Please try again.");
        closeModal();
      } else {
        message.error(
          "An intermittent issue occurred. Please contact the Support Team!",
        );
        closeModal();
      }
    } else {
      message.error(response?.message || "Failed to verify caller ID status.");
      closeModal();
    }
  } catch (error) {
    console.error("An error occurred while getting caller ID by ID: ", error);
    message.error("Failed to verify caller ID. Please try again.");
    closeModal();
  }
};

const toCreateCallerID = async () => {
  try {
    if (getCountryCode(formData.phoneNumber) === countryMapping.IN) {
      message.error(
        "Outbound caller ID selection is disabled for +91 numbers due to Indian telecom regulations.",
      );
      return;
    }

    verifying.value = true;

    const payload = {
      provider: props.provider,
      caller_id_number: formData.phoneNumber,
      extension: formData.extension,
    };

    const response = await CallService.createCallerID(payload);
    if (response?.success) {
      const data = response?.data;

      if (data?.state === addCallerIDStates.NEW) {
        formData.callerIDID = data?.callerid_id;
        formData.validationCode = data?.validation_code;
        formData.verificationUUID = data?.verification_uuid;
        currentStep.value = addCallerIDSteps.VERIFICATION;
      } else {
        message.success(data?.message || "Successfully verified!");
        eventBus.emit("caller-id-added");
        closeModal();
      }
    } else {
      message.error("An error occurred while creating Caller ID");
    }
  } catch (error) {
    console.error("An error occurred while creating caller ID: ", error);
  } finally {
    verifying.value = false;
  }
};

watch(
  () => props.visible,
  (newVal) => {
    internalVisible.value = newVal;
  },
);

watch(
  () => internalVisible.value,
  (newVal) => {
    emit("update:visible", newVal);
    if (!newVal) {
      resetForm();
    }
  },
);
</script>
